import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import apiService from '../../../services/apiService';

// واجهة عنصر الرسوم
interface Fee {
  id: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;
}

// واجهة مجموعة الرسوم
interface FeeGroup {
  id: string;
  name: string;
  description?: string;
  fees: Fee[];
  usageCount?: number;
}

const FeeGroupsList: React.FC = () => {
  const theme = useTheme();
  
  // حالة البيانات
  const [feeGroups, setFeeGroups] = useState<FeeGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // حالة الحذف
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deletingFeeGroup, setDeletingFeeGroup] = useState<FeeGroup | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  
  // حالة التفاصيل
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [selectedFeeGroup, setSelectedFeeGroup] = useState<FeeGroup | null>(null);
  
  // تحميل البيانات
  useEffect(() => {
    fetchFeeGroups();
  }, []);
  
  // جلب بيانات مجموعات الرسوم
  const fetchFeeGroups = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get<FeeGroup[]>('/finance/accounts/fee-groups');
      setFeeGroups(response);
      setLoading(false);
    } catch (err: any) {
      console.error('خطأ في تحميل مجموعات الرسوم:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل مجموعات الرسوم');
      setLoading(false);
    }
  };
  
  // فتح حوار الحذف
  const handleOpenDeleteDialog = (feeGroup: FeeGroup) => {
    setDeletingFeeGroup(feeGroup);
    setDeleteDialogOpen(true);
  };
  
  // إغلاق حوار الحذف
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingFeeGroup(null);
  };
  
  // فتح حوار التفاصيل
  const handleOpenDetailsDialog = (feeGroup: FeeGroup) => {
    setSelectedFeeGroup(feeGroup);
    setDetailsDialogOpen(true);
  };
  
  // إغلاق حوار التفاصيل
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedFeeGroup(null);
  };
  
  // حذف مجموعة رسوم
  const handleDeleteFeeGroup = async () => {
    if (!deletingFeeGroup) return;
    
    setDeleting(true);
    
    try {
      await apiService.delete(`/finance/accounts/fee-groups/${deletingFeeGroup.id}`);
      
      // تحديث القائمة
      setFeeGroups(prev => prev.filter(group => group.id !== deletingFeeGroup.id));
      
      setDeleting(false);
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('خطأ في حذف مجموعة الرسوم:', err);
      setError(err.response?.data?.message || 'حدث خطأ في حذف مجموعة الرسوم');
      setDeleting(false);
    }
  };
  
  // الحصول على أيقونة نوع الرسم
  const getFeeTypeIcon = (type: 'fixed' | 'percentage' | 'tax') => {
    switch (type) {
      case 'fixed': return <CurrencyExchangeIcon fontSize="small" />;
      case 'percentage': return <PercentIcon fontSize="small" />;
      case 'tax': return <ReceiptIcon fontSize="small" />;
      default: return <CurrencyExchangeIcon fontSize="small" />;
    }
  };
  
  // الحصول على نص نوع الرسم
  const getFeeTypeText = (type: 'fixed' | 'percentage' | 'tax'): string => {
    switch (type) {
      case 'fixed': return 'مبلغ ثابت';
      case 'percentage': return 'نسبة مئوية';
      case 'tax': return 'ضريبة';
      default: return '';
    }
  };
  
  // تنسيق قيمة الرسم للعرض
  const formatFeeValue = (type: 'fixed' | 'percentage' | 'tax', value: number): string => {
    if (type === 'fixed') {
      return value.toLocaleString('ar-SA', { style: 'currency', currency: 'SAR' });
    } else {
      return `${value}%`;
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            مجموعات الرسوم
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/finance/accounts/fee-groups/new"
          >
            إنشاء مجموعة جديدة
          </Button>
        </Box>
        
        {/* رسائل الخطأ */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box>
            {feeGroups.length === 0 ? (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    لا توجد مجموعات رسوم
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    يمكنك إنشاء مجموعة رسوم جديدة بالنقر على زر "إنشاء مجموعة جديدة"
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    component={Link}
                    to="/finance/accounts/fee-groups/new"
                    sx={{ mt: 2 }}
                  >
                    إنشاء مجموعة جديدة
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <List>
                {feeGroups.map((group) => (
                  <ListItem
                    key={group.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                      p: 2
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
                          {group.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {group.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {group.description}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1, gap: 0.5 }}>
                            {group.fees.map((fee) => (
                              <Chip
                                key={fee.id}
                                icon={getFeeTypeIcon(fee.type)}
                                label={`${fee.name}: ${formatFeeValue(fee.type, fee.value)}`}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction sx={{ display: 'flex' }}>
                      <Tooltip title="عرض التفاصيل">
                        <IconButton edge="end" aria-label="عرض التفاصيل" onClick={() => handleOpenDetailsDialog(group)} sx={{ mr: 1 }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="تحرير">
                        <IconButton edge="end" aria-label="تحرير" component={Link} to={`/finance/accounts/fee-groups/${group.id}`} sx={{ mr: 1 }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          edge="end"
                          aria-label="حذف"
                          onClick={() => handleOpenDeleteDialog(group)}
                          disabled={Boolean(group.usageCount && group.usageCount > 0)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Paper>
      
      {/* حوار حذف مجموعة رسوم */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف مجموعة الرسوم "{deletingFeeGroup?.name}"؟
            لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            إلغاء
          </Button>
          <Button 
            onClick={handleDeleteFeeGroup} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'جاري الحذف...' : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* حوار تفاصيل مجموعة الرسوم */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          تفاصيل مجموعة الرسوم
        </DialogTitle>
        <DialogContent>
          {selectedFeeGroup && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedFeeGroup.name}
              </Typography>
              
              {selectedFeeGroup.description && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedFeeGroup.description}
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                الرسوم المضمنة:
              </Typography>
              
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                {selectedFeeGroup.fees.map((fee) => (
                  <ListItem key={fee.id} sx={{ py: 1 }}>
                    <ListItemIcon>
                      {getFeeTypeIcon(fee.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={fee.name}
                      secondary={`${getFeeTypeText(fee.type)}: ${formatFeeValue(fee.type, fee.value)}`}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  عند تطبيق هذه المجموعة، سيتم خصم الرسوم من حساب المدين فقط.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog} color="primary">
            إغلاق
          </Button>
          {selectedFeeGroup && (
            <Button 
              component={Link} 
              to={`/finance/accounts/fee-groups/${selectedFeeGroup.id}`} 
              color="primary" 
              variant="contained"
              onClick={handleCloseDetailsDialog}
            >
              تحرير
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeGroupsList;